import { useEffect, useMemo, useState } from "react";
import { Dimmer, Grid, Loader, Message, Table } from "semantic-ui-react";
import { formatNumberToScientific } from "smart-decimal-format";
import {
  useGetAverageProductSummary,
  useGetProductSummaryHistoryById
} from "../../../../api/productSummary";
import { images } from "../../../../assets/images";
import { DropDown } from "../../../../components";
import { CommonTable } from "../../../../components/commonTable/CommonTable";
import { ImageView } from "../../../../components/ImageView/ImageView";
import ProgressBar from "../../../../components/progressBar/ProgressBar";
import { TitleView } from "../../../../components/titleView/TitleView";
import {
  COMPANY_NAME,
  DISABLE_IS_ROOT_PRODUCT_BUTTON,
  ImpactAssessmentMethodText,
  NOT_APPLICABLE,
  ProductSummaryTableCol
} from "../../../../config/constants";
import { ROLES } from "../../../../config/permission-maps";
import { getUserRolesInLocal } from "../../../../utils/cacheStorage";
import { humanize } from "../../../../utils/utils";

interface DropdownItem {
  _id?: string;
  createdAt?: string;
}

interface ProductDetails {
  _id?: string;
  is_average_product?: boolean;
  product_image_path?: string;
  product_name?: string;
}

interface ProductImpactTabProps {
  productDetails?: ProductDetails;
  productSummaryDropdownData?: { data?: DropdownItem[] };
  isProductSummaryHistoryLoading?: boolean;
}

const ProductImpactTab: React.FC<ProductImpactTabProps> = ({
  productDetails,
  productSummaryDropdownData,
  isProductSummaryHistoryLoading
}) => {
  const localUserRole = getUserRolesInLocal();
  const checkIsUserSupplier = localUserRole?.[0] === ROLES.supplier;
  const [productSummaryId, setProductSummaryId] = useState<string>("");
  const [productSummary, setProductSummary] = useState<any>(null);

  const productId = productDetails?._id ?? "";
  const productIsAverage = !DISABLE_IS_ROOT_PRODUCT_BUTTON
    ? productDetails?.is_average_product || false
    : false;

  const selectedProductSummaryId = useMemo(() => {
    if (productSummaryId) return productSummaryId;
    return productSummaryDropdownData?.data?.[0]?._id ?? "";
  }, [productSummaryId, productSummaryDropdownData?.data]);

  const { measure: climateChangeMeasure, unit: climateChangeUnit } =
    productSummary?.climateChange || {};

  const cycleStageData: Record<string, any> = productSummary?.cycleStages ?? {};
  const totalImpact = Array.isArray(productSummary?.totalImpact) ? productSummary!.totalImpact : [];

  const cycleStages = useMemo(
    () => Object.entries(cycleStageData).map(([key, value]) => ({ key, value })),
    [cycleStageData]
  );

  // Query for historical product summary (non-average products)
  const { data: historicalData, isFetching: isProductSummaryDataLoading } =
    useGetProductSummaryHistoryById(
      { productSummaryId: selectedProductSummaryId },
      {
        enabled: Boolean(selectedProductSummaryId) && !productIsAverage
      }
    );

  // Query for average product summary (average products)
  const { data: averageData, isFetching: isAverageProductSummaryDataLoading } =
    useGetAverageProductSummary(
      {
        productId,
        productIsAverage: productIsAverage
      },
      {
        enabled: Boolean(productId) && Boolean(productIsAverage)
      }
    );

  // Modern React Query pattern: Use useEffect for data synchronization
  // This replaces the deprecated onSuccess callbacks
  useEffect(() => {
    if (productIsAverage && averageData) {
      setProductSummary(averageData);
    } else if (!productIsAverage && historicalData) {
      setProductSummary(historicalData);
    }
  }, [productIsAverage, averageData, historicalData]);

  const formatDateText = (iso?: string): string => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 19).replace("T", " ");
  };

  const calculatedDateData = useMemo(() => {
    return (
      productSummaryDropdownData?.data?.map((item: DropdownItem) => ({
        key: item._id ?? "",
        value: item._id ?? "",
        text: formatDateText(item.createdAt)
      })) ?? []
    );
  }, [productSummaryDropdownData?.data]);

  const tableRows = useMemo(() => {
    return totalImpact.map((item: any, index: number) => (
      <Table.Row className="tbleR" key={item.indicator ?? index}>
        <Table.Cell>
          <p>{item.indicator ?? "-"}</p>
        </Table.Cell>
        <Table.Cell>
          <p>{formatNumberToScientific(item.measure, 5) ?? "-"}</p>
        </Table.Cell>
        <Table.Cell>
          <p>{item.unit ?? "-"}</p>
        </Table.Cell>
        <Table.Cell>
          <Grid>
            <Grid.Column
              computer={16}
              tablet={16}
              mobile={16}
              className="alignRight addViewDetails"
            />
          </Grid>
        </Table.Cell>
      </Table.Row>
    ));
  }, [totalImpact]);

  if (isProductSummaryDataLoading || isAverageProductSummaryDataLoading) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  return (
    <>
      {productSummary ? (
        !checkIsUserSupplier ? (
          <>
            <Grid className="backgroundMain productProgressMain">
              <Grid.Column computer={16} tablet={16} mobile={16}>
                <Grid>
                  <Grid.Column computer={4} tablet={16} mobile={16} className="productDetailsView">
                    <div>
                      <DropDown
                        labelName="Calculated on"
                        placeholder="Select Date"
                        currentData={calculatedDateData}
                        defaultValue={selectedProductSummaryId}
                        handleChangeState={(_e, data) => setProductSummaryId(data.value as string)}
                        loading={isProductSummaryHistoryLoading}
                      />
                      <ImageView
                        addRandomId={false}
                        src={productDetails?.product_image_path || images.notFound}
                        customImageView="customImageViewProductSummery"
                        height={150}
                      />
                      <div className="productDetailsViewOther">
                        <p className="productNameDetails">{productDetails?.product_name}</p>
                      </div>
                    </div>
                  </Grid.Column>

                  <Grid.Column computer={6} tablet={16} mobile={16}>
                    <Grid className="backgroundMain">
                      <Grid.Column computer={16} tablet={16} mobile={16}>
                        <TitleView title="Climate Change Impact" />
                        <p className="viewNumberOfClimate">
                          {typeof climateChangeMeasure === "number"
                            ? climateChangeMeasure.toFixed(2)
                            : climateChangeMeasure ?? "-"}
                        </p>
                        <p className="viewNumberOfClimateSub">
                          {(climateChangeUnit ?? "") + " per kg"}
                        </p>
                        <div className="climateDetailsView">
                          {COMPANY_NAME}
                          {ImpactAssessmentMethodText}
                        </div>
                        <h5 className="viewBottomClimateDetails" hidden>
                          DQR : 3.61
                        </h5>
                      </Grid.Column>
                    </Grid>
                  </Grid.Column>

                  <Grid.Column computer={6} tablet={16} mobile={16}>
                    <Grid className="backgroundMain">
                      <Grid.Column computer={16} tablet={16} mobile={16}>
                        <TitleView title="Impact by life cycle stages" />
                        {cycleStages.map((stage, idx) => (
                          <ProgressBar
                            key={stage.key ?? idx}
                            imageView
                            // images is a mapping; index into it safely
                            image={(images as any)[stage.key]}
                            title={humanize(stage.key)}
                            count={
                              stage.value === NOT_APPLICABLE
                                ? stage.value
                                : `${parseFloat(String(stage.value)).toFixed(2)}%`
                            }
                            percentage={stage.value === NOT_APPLICABLE ? 0 : Number(stage.value)}
                          />
                        ))}
                      </Grid.Column>
                    </Grid>
                  </Grid.Column>
                </Grid>
              </Grid.Column>
            </Grid>

            <CommonTable tableHeaderData={ProductSummaryTableCol}>{tableRows}</CommonTable>
          </>
        ) : (
          <Message color="yellow">Calculated: Upgrade to see results</Message>
        )
      ) : (
        <Message color="yellow">Data collection in progress</Message>
      )}
    </>
  );
};

export default ProductImpactTab;
